import { StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Inject, Injectable, Injector } from '@angular/core';
import { State, Store } from '@ngxs/store';
import { combineLatest, Subscription } from 'rxjs';
import { filter, first, map, take } from 'rxjs/operators';
import { IProductContextCase, ProductCategoryNames } from 'src/app/models/case';
import { BaseProductService } from 'src/app/services/+products/-base-product.service';

import {
    IInterviewEvent,
    IInterviewHandler,
    InterviewEventType
} from '../../shared/interview-new/models/interview-handler.models';
import { ConfigurationService } from '../configuration/configuration.service';
import { IServiceFactoryService, serviceFactoryInjectToken } from '../global/service-factory.models';
import {
    InterviewProductDataProviderService
} from '../interview-providers/abstract-interview-product-data-provider.service';
import { MemberService } from '../member/member.service';
import { ProductService } from '../product/product.service';
import { TaskOrchestratorService } from '../task-orchestrator/task-orchestrator.service';
import { CommandParserService } from '../task/command/command-parser.service';
import { UserService } from '../user/user.service';
import { BaseProductStateModel } from './base-product-state.models';

@StateRepository()
@State<any>({
    name: 'productOrchestratorState',
    defaults: null
})
@Injectable()
export class ProductOrchestratorService extends NgxsDataRepository<any> {

    productServices: BaseProductService<BaseProductStateModel>[] = [];
    routeSubscription: Subscription;
    interviewEvent$$: Subscription;
    primaryInterviewHandler: IInterviewHandler;

    constructor(
        private readonly store: Store,
        @Inject(serviceFactoryInjectToken) protected serviceFactory: IServiceFactoryService,
        protected productService: ProductService,
        private readonly memberService: MemberService,
        private readonly taskOrchestratorService: TaskOrchestratorService,
        private readonly injector: Injector
    ) {
        super();
        this.configureGlobalListeners();
    }

    getProductServiceByCaseId(caseId: any): BaseProductService<BaseProductStateModel> {
        return this.productServices.find(service => service.currentCaseSync?.id == caseId);
    }

    async ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();

        this.taskOrchestratorListener();

        await combineLatest([this.store.select<string>(UserService.userIdentifier), this.store.select<boolean>(ConfigurationService.initializedSelector)]).pipe(filter(([userIdentifier, configInitialized]) => !!userIdentifier && configInitialized), first()).toPromise();
        Object.getOwnPropertyNames(ProductCategoryNames).forEach(productCategoryName => {
            const productServiceItem = this.serviceFactory.getProductService(ProductCategoryNames[productCategoryName]);

            this.productServices.push(productServiceItem);
        });
    }

    configureGlobalListeners() {

    }

    setPrimaryInterview(handler: IInterviewHandler) {
        this.primaryInterviewHandler = handler;
        this.configureListeners();

    }

    configureListeners() {
        if (this.interviewEvent$$) {
            try {
                this.interviewEvent$$.unsubscribe();
            } catch {

            }
        }

        this.interviewEvent$$ = this.primaryInterviewHandler.events.lifeCycleEvent$.asObservable().subscribe((interviewEvent: IInterviewEvent) => {
            switch (interviewEvent.type) {
                case InterviewEventType.Initialized:
                    this.memberService.showMemberSessionSurvey = true;
                    this.processInterviewInitialization(interviewEvent);
                    break;
                case InterviewEventType.Next:

                case InterviewEventType.Previous:
                // case 'PREV':

                case InterviewEventType.Goto:
                case InterviewEventType.Postback: // 'RELOAD'
                    this.processInterviewEvaluation(interviewEvent);
                    break;
                case InterviewEventType.Finished: // 'FINISHED'
                    this.processInterviewFinished(interviewEvent);
                    break;
                case InterviewEventType.Done: // Every interview process is already done. No more actions needed
                    this.processInterviewDone(interviewEvent);
                    break;
            }
        });
    }

    taskOrchestratorListener() {
        // fill interviews for each get product service
        this.interviewEvent$$ = this.taskOrchestratorService.tasks$.subscribe(tasks => {
            this.productServices.forEach(productServiceItem => productServiceItem.clearInterviews()); //
            tasks?.filter(task => task.active && task.type == 'INTERVIEW').forEach(task => {

                const [, , category, type] = task.id.split('.');

                const productServiceItem = this.productServices.find(productServiceItem => productServiceItem.isSameCategory(category));
                if (productServiceItem) {
                    const interviewConfig = CommandParserService.getInterviewCommandConfig(task.command);
                    interviewConfig.type = type;
                    productServiceItem.addInterview(interviewConfig);
                }
            });
        });
    }

    processInterviewInitialization(interviewEvent: IInterviewEvent) {

        const extraProductServices = this.productServices.filter(productServiceItem => {
            if (!interviewEvent || !interviewEvent.handler || !interviewEvent.handler.settings) {
                return false;
            }

            return productServiceItem !== (this.primaryInterviewHandler.settings.dataProvider as InterviewProductDataProviderService).productService;
        });

        extraProductServices.forEach(async productServiceItem => {
            await productServiceItem.initializeSecondaryInterview(interviewEvent.handler.settings, this.injector);
        });
    }

    getServiceByCaseId(caseId: any): BaseProductService<BaseProductStateModel> {
        return this.productServices.find(productServiceItem => productServiceItem?.currentCaseSync?.id == caseId);
    }

    processInterviewEvaluation(interviewEvent: IInterviewEvent) {
        const extraProductServices = this.productServices.filter(productServiceItem => productServiceItem !== (interviewEvent.handler.settings.dataProvider as InterviewProductDataProviderService).productService);
        extraProductServices.forEach(async productServiceItem => {
            const primaryVariables = interviewEvent.handler.prepareVariablesToEvaluate();
            await productServiceItem.evaluateSecondaryInterview(interviewEvent.handler.settings, primaryVariables);
        });
    }

    processInterviewFinished(interviewEvent: IInterviewEvent) {
        const extraProductService = this.productServices.find(productServiceItem => productServiceItem === (interviewEvent.handler.settings.dataProvider as InterviewProductDataProviderService).productService);
    }

    processInterviewDone(interviewEvent: IInterviewEvent) {
        const extraProductServices = this.productServices.filter(productServiceItem => productServiceItem !== (interviewEvent.handler.settings.dataProvider as InterviewProductDataProviderService).productService);
        extraProductServices.forEach(async productServiceItem => {
            await productServiceItem.interviewHandler?.clear();
        });
    }

    async productAvailable(categoryName: string) {
        const productService = this.productServices.find(item => item.productCategory === categoryName);

        return await productService.status$.pipe(take(1), map(availability => {
            return availability.active;
        })).toPromise();
    }

    // TODO add context level flag to refresh only the matching cases
    async refreshAll() {
        const refreshList: Promise<IProductContextCase>[] = [];
        this.productServices.forEach(async productServiceItem => {
            refreshList.push(productServiceItem.refreshCase().toPromise());
        });

        Promise.all(refreshList);
    }
}
